import { useEffect, useMemo, useRef } from 'react';
import { useEntityContext } from '@sierralabs/entity-ui';
import { QueryManager, mergeExpressionMaps } from '@sierralabs/query-core';
import { defaultQueryBuilderPlugins, getQuerySelectValue, isPaginatedList, useRunQuery } from '@sierralabs/query-ui';
import { parseTaskStatus } from '@sierralabs/workflow-core';
export function createQueryCsvData(options) {
    const { queryData, selectedFields, taskStatusFields, expressionMap, queryAliasColumnMap } = options;
    // add labels to CSV first row
    const labels = selectedFields.map(field => {
        var _a;
        const columnMap = queryAliasColumnMap.find(cm => cm.path === field);
        return ((_a = columnMap === null || columnMap === void 0 ? void 0 : columnMap.column) === null || _a === void 0 ? void 0 : _a.displayName) || field || '';
    });
    // process all data rows
    // filter selects based on selected fields to be exported
    // sort based on the order of selectFields to properly match column data
    const selects = (options.selects || [])
        .filter(select => selectedFields.includes(select.selection))
        .sort((a, b) => {
        const aIndex = selectedFields.indexOf(a.selection);
        const bIndex = selectedFields.indexOf(b.selection);
        return aIndex - bIndex;
    });
    // handle values from all return types
    const data = isPaginatedList(queryData)
        ? queryData.data
        : Array.isArray(queryData)
            ? queryData
            : queryData
                ? [queryData]
                : [];
    const rows = data.map(item => {
        const row = selects.map(select => {
            let value = getQuerySelectValue({ item, select, expressionMap });
            if (!value) {
                return '""';
            }
            value = value.toString();
            value = (taskStatusFields === null || taskStatusFields === void 0 ? void 0 : taskStatusFields.includes(select.selection))
                ? parseTaskStatus(value)
                : value;
            return `"${value.replace(/"/g, '""')}"`;
        });
        return row;
    });
    const csvRows = [labels, ...rows];
    const csvData = csvRows.map(row => row.join(',')).join('\r\n');
    return csvData;
}
export const QueryCsvExport = props => {
    var _a;
    const { query, returnType, expressionMap, queryBuilderPlugins, download, onError } = props;
    const downloadRef = useRef(download);
    downloadRef.current = download;
    const onErrorRef = useRef(onError);
    onErrorRef.current = onError;
    const { entities } = useEntityContext();
    const { queryData, runQueryError, runQuery, runQueryGetOne, runPaginatedQuery } = useRunQuery();
    const runQueryFn = returnType === 'paginated'
        ? runPaginatedQuery
        : returnType === 'one'
            ? runQueryGetOne
            : runQuery;
    // evaluate only when items changed
    const selectedFields = useMemo(() => {
        return props.selectedFields;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.selectedFields.join(',')]);
    const taskStatusFields = useMemo(() => {
        return props.taskStatusFields || [];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [(_a = props.taskStatusFields) === null || _a === void 0 ? void 0 : _a.join(',')]);
    const queryManager = useMemo(() => {
        return new QueryManager(query, entities || [], queryBuilderPlugins || defaultQueryBuilderPlugins);
    }, [entities, query, queryBuilderPlugins]);
    useEffect(() => {
        var _a;
        const config = !query.config
            ? undefined
            : expressionMap
                ? mergeExpressionMaps(query.config, expressionMap)
                : query.config;
        if (config) {
            runQueryFn(config);
        }
        else {
            (_a = onErrorRef.current) === null || _a === void 0 ? void 0 : _a.call(onErrorRef, {
                statusCode: 400,
                error: 'Bad Request',
                message: 'Missing Query configuration.'
            });
        }
    }, [query, expressionMap, runQueryFn]);
    useEffect(() => {
        var _a;
        if (runQueryError) {
            (_a = onErrorRef.current) === null || _a === void 0 ? void 0 : _a.call(onErrorRef, runQueryError);
        }
    }, [runQueryError]);
    // generate CSV
    useEffect(() => {
        var _a, _b, _c;
        if (!queryData) {
            return;
        }
        const csvData = createQueryCsvData({
            queryData,
            selectedFields,
            taskStatusFields,
            expressionMap: (_a = queryManager.query) === null || _a === void 0 ? void 0 : _a.config,
            selects: (_c = (_b = queryManager.query) === null || _b === void 0 ? void 0 : _b.config) === null || _c === void 0 ? void 0 : _c.selects,
            queryAliasColumnMap: queryManager.queryAliasColumnMap
        });
        downloadRef.current(csvData);
    }, [queryData, queryManager, selectedFields, taskStatusFields]);
    return null;
};
export default QueryCsvExport;
//# sourceMappingURL=QueryCsvExport.js.map